import type { RefetchKey, StaleDataEventPayload } from './socket.types';
import { getRefetchKeysFromStaleData } from './stale-data';

type StaleDataPayload = StaleDataEventPayload;
type StaleDataRefetchHandler = (
  payload: StaleDataPayload,
  keys: RefetchKey[],
) => void | Promise<void>;

const handlersByKey = new Map<RefetchKey, Set<StaleDataRefetchHandler>>();

const getHandlers = (key: RefetchKey) => {
  const existing = handlersByKey.get(key);

  if (existing) {
    return existing;
  }

  const created = new Set<StaleDataRefetchHandler>();
  handlersByKey.set(key, created);
  return created;
};

export const registerStaleDataRefetchHandler = (
  key: RefetchKey,
  handler: StaleDataRefetchHandler,
): (() => void) => {
  const handlers = getHandlers(key);
  handlers.add(handler);

  return () => {
    const currentHandlers = handlersByKey.get(key);

    if (!currentHandlers) {
      return;
    }

    currentHandlers.delete(handler);

    if (currentHandlers.size === 0) {
      handlersByKey.delete(key);
    }
  };
};

export const dispatchStaleDataRefetchKeys = (
  payload: StaleDataPayload,
  keys: RefetchKey[],
): void => {
  const dispatchedHandlers = new Set<StaleDataRefetchHandler>();

  keys.forEach((key) => {
    const handlers = handlersByKey.get(key);

    if (!handlers || handlers.size === 0) {
      return;
    }

    handlers.forEach((handler) => {
      if (dispatchedHandlers.has(handler)) {
        return;
      }

      dispatchedHandlers.add(handler);

      void Promise.resolve(handler(payload, keys)).catch((error) => {
        console.error(`[stale-data] Failed to handle refetch key "${key}"`, error);
      });
    });
  });
};

export const dispatchStaleDataPayload = (payload: StaleDataPayload): RefetchKey[] => {
  const keys = getRefetchKeysFromStaleData(payload);
  dispatchStaleDataRefetchKeys(payload, keys);
  return keys;
};
